import type {
  ChecklistItemEntity,
  CompletionEntity,
  ProjectEntity,
  TagEntity,
  TaskEntity,
  TaskTagEntity,
} from '../../../entities/task/model/task.types';
import { PRIORITY_RANK } from '../../../shared/lib/priority';
import { occursOnDate, isCalendarDate } from './recurrence';
import {
  hasDescription,
  hasRecurrence,
  isIdea,
  isRegularTask,
  type TaskFilters,
  type TaskListItem,
  type TaskListKind,
  type TasksData,
} from './task.types';

function compareStrings(a: string, b: string): number {
  if (a === b) {
    return 0;
  }
  return a < b ? -1 : 1;
}

function dueDateOf(item: TaskListItem): string | null {
  return item.occurrenceDate ?? item.task.due_date;
}

/**
 * Сортировка: приоритет urgent→high→medium→low; при равном — ближе срок;
 * в один день время раньше без времени выше; выполненные внизу.
 */
export function compareTaskListItems(a: TaskListItem, b: TaskListItem): number {
  const aDone = a.isCompleted ? 1 : 0;
  const bDone = b.isCompleted ? 1 : 0;
  if (aDone !== bDone) {
    return aDone - bDone;
  }

  const pa = PRIORITY_RANK[a.task.priority];
  const pb = PRIORITY_RANK[b.task.priority];
  if (pa !== pb) {
    return pa - pb;
  }

  const dateA = dueDateOf(a);
  const dateB = dueDateOf(b);

  if (dateA === null && dateB === null) {
    return compareStrings(a.task.title.toLowerCase(), b.task.title.toLowerCase());
  }
  if (dateA === null) {
    return 1;
  }
  if (dateB === null) {
    return -1;
  }
  if (dateA !== dateB) {
    return compareStrings(dateA, dateB);
  }

  const timeA = a.hasTime ? a.task.due_time : null;
  const timeB = b.hasTime ? b.task.due_time : null;
  if (timeA === null && timeB === null) {
    return compareStrings(a.task.title.toLowerCase(), b.task.title.toLowerCase());
  }
  if (timeA === null) {
    return 1;
  }
  if (timeB === null) {
    return -1;
  }
  return compareStrings(timeA, timeB);
}

export function sortTaskListItems(items: TaskListItem[]): TaskListItem[] {
  return [...items].sort(compareTaskListItems);
}

function buildItem(args: {
  task: TaskEntity;
  occurrenceDate: string | null;
  isCompleted: boolean;
  data: TasksData;
}): TaskListItem {
  const { task, occurrenceDate, isCompleted, data } = args;
  const tagIds = data.taskTags
    .filter((link) => link.task_id === task.id)
    .map((link) => link.tag_id);
  const tags = data.tags.filter((tag) => tagIds.includes(tag.id));
  const checklistItems = data.checklistItems.filter((item) => item.task_id === task.id);
  const checklistDone = checklistItems.filter((item) => item.done).length;
  const checklistTotal = checklistItems.length;

  return {
    task,
    tags,
    checklistDone,
    checklistTotal,
    occurrenceDate,
    isCompleted,
    isRecurring: hasRecurrence(task),
    hasDescription: hasDescription(task),
    hasChecklist: checklistTotal > 0,
    hasTime: task.due_time !== null && !task.all_day,
  };
}

export function isRecurringCompletedOn(
  task: TaskEntity,
  date: string,
  completions: CompletionEntity[],
): boolean {
  return completions.some(
    (completion) =>
      completion.task_id === task.id &&
      completion.occurrence_date === date &&
      completion.deleted_at === null,
  );
}

function activeCompletions(data: TasksData): CompletionEntity[] {
  return data.completions.filter((completion) => completion.deleted_at === null);
}

/**
 * Начало серии для occursOnDate: due_date, иначе дата создания (UTC-день).
 * Без опоры «календарь повтора» считать нельзя.
 */
function seriesStart(task: TaskEntity): string | null {
  if (task.due_date !== null && task.due_date.length === 10) {
    return task.due_date;
  }
  const created = task.created_at.slice(0, 10);
  return isCalendarDate(created) ? created : null;
}

function mapTasks(
  tasks: TaskEntity[],
  data: TasksData,
  occurrenceDateFor: (task: TaskEntity) => string | null,
  isCompletedFor: (task: TaskEntity, occurrenceDate: string | null) => boolean,
): TaskListItem[] {
  return tasks.map((task) => {
    const occurrenceDate = occurrenceDateFor(task);
    return buildItem({
      task,
      occurrenceDate,
      isCompleted: isCompletedFor(task, occurrenceDate),
      data,
    });
  });
}

function nonRecurringCompleted(task: TaskEntity): boolean {
  return task.completed;
}

/** Идеи: is_idea=true, не archived, не deleted. */
export function selectIdeasTasks(data: TasksData): TaskListItem[] {
  const tasks = data.tasks.filter(
    (task) => task.deleted_at === null && task.archived_at === null && isIdea(task),
  );
  return sortTaskListItems(
    mapTasks(tasks, data, () => null, (task) => nonRecurringCompleted(task)),
  );
}

export interface TodayGroups {
  overdue: TaskListItem[];
  today: TaskListItem[];
  undated: TaskListItem[];
}

/**
 * «Сегодня»:
 * 1) Просрочено — due_date < сегодня, не completed, не archived, НЕ recurring, не is_idea;
 * 2) Сегодня — due_date == сегодня ИЛИ recurring-серия с occurrence сегодня;
 *    выполненные внизу (отдельным блоком в UI);
 * 3) Без даты — due_date == null, отдельный блок внизу.
 */
export function selectTodayTasks(data: TasksData, today: string): TodayGroups {
  const overdueTasks = data.tasks.filter(
    (task) =>
      isRegularTask(task) &&
      !hasRecurrence(task) &&
      task.due_date !== null &&
      task.due_date < today &&
      !task.completed,
  );
  const overdue = sortTaskListItems(
    mapTasks(overdueTasks, data, (task) => task.due_date, () => false),
  );

  const completions = activeCompletions(data);
  const todayTasks = data.tasks.filter((task) => {
    if (!isRegularTask(task)) {
      return false;
    }
    // Повторяющиеся: ТОЛЬКО occurrence по правилу (не голое due_date === today).
    if (hasRecurrence(task)) {
      const start = seriesStart(task);
      return start !== null && occursOnDate(task.recurrence_rule, start, today);
    }
    if (task.due_date === today) {
      return true;
    }
    // Выполненные с due_date < сегодня — не пропадают, попадают в «Выполненные».
    if (task.due_date !== null && task.due_date < today && task.completed) {
      return true;
    }
    return false;
  });
  // Для повторяющихся occurrence = день списка (сегодня), не start_date серии.
  const todayList = sortTaskListItems(
    mapTasks(
      todayTasks,
      data,
      (task) => (hasRecurrence(task) ? today : task.due_date),
      (task, occurrenceDate) => {
        if (hasRecurrence(task)) {
          return isRecurringCompletedOn(task, occurrenceDate ?? today, completions);
        }
        return task.completed;
      },
    ),
  );

  const undatedTasks = data.tasks.filter((task) => {
    if (!isRegularTask(task) || task.due_date !== null) {
      return false;
    }
    // Без даты: только если не серия ИЛИ сегодня occurrence (по created_at как start).
    if (!hasRecurrence(task)) {
      return true;
    }
    const start = seriesStart(task);
    return start !== null && occursOnDate(task.recurrence_rule, start, today);
  });
  const undated = sortTaskListItems(
    mapTasks(
      undatedTasks,
      data,
      () => null,
      (task, occurrenceDate) => {
        if (hasRecurrence(task)) {
          return isRecurringCompletedOn(task, occurrenceDate ?? today, completions);
        }
        return task.completed;
      },
    ),
  );

  return { overdue, today: todayList, undated };
}

/** «Завтра»: due_date == завтра ИЛИ recurring с occurrence завтра. Без undated, без is_idea. */
export function selectTomorrowTasks(data: TasksData, tomorrow: string): TaskListItem[] {
  const completions = activeCompletions(data);
  const tasks = data.tasks.filter((task) => {
    if (!isRegularTask(task)) {
      return false;
    }
    if (hasRecurrence(task)) {
      const start = seriesStart(task);
      return start !== null && occursOnDate(task.recurrence_rule, start, tomorrow);
    }
    return task.due_date === tomorrow;
  });
  return sortTaskListItems(
    mapTasks(
      tasks,
      data,
      (task) => (hasRecurrence(task) ? tomorrow : task.due_date),
      (task, occurrenceDate) => {
        if (hasRecurrence(task)) {
          return isRecurringCompletedOn(task, occurrenceDate ?? tomorrow, completions);
        }
        return task.completed;
      },
    ),
  );
}

export function selectOverdueTasks(data: TasksData, today: string): TaskListItem[] {
  const { overdue } = selectTodayTasks(data, today);
  return overdue;
}

/** Архив: только задачи (не идеи), archived_at !== null. */
export function selectArchiveTasks(data: TasksData): TaskListItem[] {
  const tasks = data.tasks.filter((task) => task.archived_at !== null && !isIdea(task));
  return sortTaskListItems(
    mapTasks(tasks, data, (task) => task.due_date, (task) => task.completed),
  );
}

export function filterTaskListItems(
  items: TaskListItem[],
  filters: TaskFilters,
): TaskListItem[] {
  const search = filters.search.trim().toLowerCase();

  return items.filter((item) => {
    if (filters.hideCompleted && item.isCompleted) {
      return false;
    }
    if (filters.priority !== 'all' && item.task.priority !== filters.priority) {
      return false;
    }
    if (filters.projectId !== null && item.task.project_id !== filters.projectId) {
      return false;
    }
    if (filters.tagIds.length > 0) {
      const itemTagIds = item.tags.map((tag) => tag.id);
      const hasAll = filters.tagIds.every((tagId) => itemTagIds.includes(tagId));
      if (!hasAll) {
        return false;
      }
    }
    if (search.length > 0 && !item.task.title.toLowerCase().includes(search)) {
      return false;
    }
    return true;
  });
}

export function hasActiveFilters(filters: TaskFilters): boolean {
  return (
    filters.tagIds.length > 0 ||
    filters.priority !== 'all' ||
    filters.search.trim().length > 0 ||
    filters.hideCompleted ||
    filters.projectId !== null
  );
}

export function selectListKindTasks(
  data: TasksData,
  kind: TaskListKind,
  today: string,
  tomorrow: string,
): { overdue: TaskListItem[]; list: TaskListItem[]; undated: TaskListItem[] } {
  switch (kind) {
    case 'today': {
      const groups = selectTodayTasks(data, today);
      return { overdue: groups.overdue, list: groups.today, undated: groups.undated };
    }
    case 'tomorrow':
      return { overdue: [], list: selectTomorrowTasks(data, tomorrow), undated: [] };
    case 'overdue':
      return { overdue: [], list: selectOverdueTasks(data, today), undated: [] };
    case 'archive':
      return { overdue: [], list: selectArchiveTasks(data), undated: [] };
    default: {
      const exhaustive: never = kind;
      throw new Error(`Неизвестный список: ${String(exhaustive)}`);
    }
  }
}

export function buildTaskListItem(
  task: TaskEntity,
  data: TasksData,
  occurrenceDate: string | null,
  isCompleted: boolean,
): TaskListItem {
  return buildItem({ task, occurrenceDate, isCompleted, data });
}

export function listTagOptions(data: TasksData): TagEntity[] {
  return [...data.tags].sort((a, b) => a.name.localeCompare(b.name, 'ru'));
}

export function listProjectOptions(data: TasksData): ProjectEntity[] {
  return [...data.projects].sort((a, b) => a.name.localeCompare(b.name, 'ru'));
}

/** Задачи проекта: не архив, не идеи; split overdue / dated / undated. */
export function selectProjectTasks(
  data: TasksData,
  projectId: string,
  today: string,
): { overdue: TaskListItem[]; list: TaskListItem[]; undated: TaskListItem[] } {
  const tasks = data.tasks.filter(
    (task) => task.project_id === projectId && isRegularTask(task),
  );
  const completions = activeCompletions(data);

  const overdueTasks = tasks.filter(
    (task) =>
      !hasRecurrence(task) &&
      task.due_date !== null &&
      task.due_date < today &&
      !task.completed,
  );
  const overdue = sortTaskListItems(
    mapTasks(overdueTasks, data, (task) => task.due_date, () => false),
  );

  const undatedTasks = tasks.filter(
    (task) => !hasRecurrence(task) && task.due_date === null,
  );
  const undated = sortTaskListItems(
    mapTasks(undatedTasks, data, () => null, (task) => task.completed),
  );

  const listTasks = tasks.filter((task) => {
    if (hasRecurrence(task)) {
      return true;
    }
    if (task.due_date === null) {
      return false;
    }
    return !(task.due_date < today && !task.completed);
  });
  const list = sortTaskListItems(
    mapTasks(
      listTasks,
      data,
      (task) => task.due_date,
      (task, occurrenceDate) => {
        if (hasRecurrence(task)) {
          return isRecurringCompletedOn(task, occurrenceDate ?? today, completions);
        }
        return task.completed;
      },
    ),
  );

  return { overdue, list, undated };
}

export function checklistForTask(
  data: TasksData,
  taskId: string,
): ChecklistItemEntity[] {
  return data.checklistItems
    .filter((item) => item.task_id === taskId)
    .sort((a, b) => a.sort_order - b.sort_order || a.id.localeCompare(b.id));
}

export function tagsForTask(data: TasksData, taskId: string): TagEntity[] {
  const tagIds = data.taskTags
    .filter((link) => link.task_id === taskId)
    .map((link) => link.tag_id);
  return data.tags.filter((tag) => tagIds.includes(tag.id));
}

export function taskTagLinks(data: TasksData, taskId: string): TaskTagEntity[] {
  return data.taskTags.filter((link) => link.task_id === taskId);
}

/** Утилита для recurrence-фильтров вне списков (не используется в Сегодня/Завтра/календаре). */
export function occursOnDateForTask(task: TaskEntity, date: string): boolean {
  return hasRecurrence(task) && occursOnDate(task.recurrence_rule, task.due_date, date);
}

export { activeCompletions };
